import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Brain,
  Sparkles,
  Search,
  BookOpen,
  TrendingUp,
  Layers,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Clock,
  Printer,
  ShieldCheck,
  RefreshCw,
  Eye,
  FileText,
  Sliders,
  RotateCcw,
  Check,
  X,
  Plus,
  HelpCircle,
  FileCheck2,
  History,
  Award
} from 'lucide-react';

export const PYQIntelligencePage: React.FC = () => {
  const { fetchWithAuth, user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'principal';

  // 10 Tabs
  type TabType =
    | 'overview'
    | 'repository'
    | 'chapter-analysis'
    | 'topic-analysis'
    | 'repeated'
    | 'trends'
    | 'patterns'
    | 'blueprint'
    | 'generator'
    | 'history';

  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Filters
  const [selectedBoard, setSelectedBoard] = useState<number>(2); // 1: HBSE, 2: CBSE
  const [selectedClass, setSelectedClass] = useState<number>(1); // 1: Class 10, 2: Class 12
  const [selectedMedium, setSelectedMedium] = useState<string>('English');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Data States
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [frequencies, setFrequencies] = useState<any[]>([]);
  const [trends, setTrends] = useState<any[]>([]);
  const [repeatedClusters, setRepeatedClusters] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [selectedChapterDetail, setSelectedChapterDetail] = useState<any>(null);

  // Blueprint Builder States
  const [blueprintTotalMarks, setBlueprintTotalMarks] = useState(80);
  const [blueprintSections, setBlueprintSections] = useState<any[]>([
    { name: 'Section A', question_count: 20, marks_per_question: 1, type: 'MCQ' },
    { name: 'Section B', question_count: 5, marks_per_question: 2, type: 'VSA' },
    { name: 'Section C', question_count: 6, marks_per_question: 3, type: 'SA' },
    { name: 'Section D', question_count: 4, marks_per_question: 5, type: 'LA' },
    { name: 'Section E', question_count: 3, marks_per_question: 4, type: 'Case Study' }
  ]);
  const [blueprintValidationMsg, setBlueprintValidationMsg] = useState<{ valid: boolean; text: string } | null>(null);

  // Generator States
  const [genMode, setGenMode] = useState('BOARD_PATTERN');
  const [genSourceMix, setGenSourceMix] = useState('PYQ_ONLY');
  const [genSchoolName, setGenSchoolName] = useState('Government Senior Secondary School, Nuh');
  const [genExamName, setGenExamName] = useState('Class 10 Mathematics Annual Examination 2026–27');
  const [genDuration, setGenDuration] = useState(180);
  const [generatedPaper, setGeneratedPaper] = useState<any>(null);
  const [qualityCheck, setQualityCheck] = useState<any>(null);
  const [showAnswerKey, setShowAnswerKey] = useState(false);

  // Replacement Modal
  const [replaceModal, setReplaceModal] = useState<{
    open: boolean;
    sectionIndex: number;
    questionIndex: number;
    question: any;
  }>({ open: false, sectionIndex: 0, questionIndex: 0, question: null });

  // Load Intelligence Data
  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Overview
      const oRes = await fetchWithAuth(`/api/pyq-intelligence/overview?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const oData = await oRes.json();
      if (oData.success) setOverview(oData);

      // 2. Questions
      const qRes = await fetchWithAuth(`/api/pyq-intelligence/questions?board_id=${selectedBoard}&class_id=${selectedClass}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`);
      const qData = await qRes.json();
      if (qData.success) setQuestions(qData.questions || []);

      // 3. Frequencies
      const fRes = await fetchWithAuth(`/api/pyq-intelligence/frequency?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const fData = await fRes.json();
      if (fData.success) setFrequencies(fData.frequencies || []);

      // 4. Trends
      const tRes = await fetchWithAuth(`/api/pyq-intelligence/trends?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const tData = await tRes.json();
      if (tData.success) setTrends(tData.trends || []);

      // 5. Repeated
      const rRes = await fetchWithAuth(`/api/pyq-intelligence/repeated?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const rData = await rRes.json();
      if (rData.success) setRepeatedClusters(rData.clusters || []);

      // 6. Patterns
      const pRes = await fetchWithAuth(`/api/pyq-intelligence/patterns?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const pData = await pRes.json();
      if (pData.success) setPatterns(pData.patterns || []);
    } catch (err) {
      console.error('Failed to load PYQ intelligence:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBoard, selectedClass, activeTab]);

  // Validate Blueprint
  const handleValidateBlueprint = async () => {
    try {
      const res = await fetchWithAuth('/api/pyq-intelligence/blueprint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: selectedBoard,
          class_id: selectedClass,
          total_marks: blueprintTotalMarks,
          sections: blueprintSections
        })
      });
      const data = await res.json();
      if (data.success) {
        setBlueprintValidationMsg({ valid: true, text: `✓ Valid Blueprint: Exactly ${data.total_marks} Marks balanced.` });
      } else {
        setBlueprintValidationMsg({ valid: false, text: data.message || 'Validation failed' });
      }
    } catch (err: any) {
      setBlueprintValidationMsg({ valid: false, text: err.message || 'Network error' });
    }
  };

  // Generate Paper
  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/pyq-intelligence/paper/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: selectedBoard,
          class_id: selectedClass,
          subject_id: 2,
          medium: selectedMedium,
          exam_name: genExamName,
          school_name: genSchoolName,
          total_marks: blueprintTotalMarks,
          duration_minutes: genDuration,
          mode: genMode,
          source_mix: genSourceMix,
          custom_sections: blueprintSections
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedPaper(data);
        // Automatically run AI Quality Check
        const qcRes = await fetchWithAuth(`/api/pyq-intelligence/paper/${data.id}/check`, { method: 'POST' });
        const qcData = await qcRes.json();
        if (qcData.success) setQualityCheck(qcData);
      } else {
        alert(data.message || 'Generation failed');
      }
    } catch (err) {
      console.error('Generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Replace Question
  const handleReplaceQuestion = async (type: string) => {
    if (!generatedPaper) return;
    try {
      const res = await fetchWithAuth(`/api/pyq-intelligence/paper/${generatedPaper.id}/replace-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_index: replaceModal.sectionIndex,
          question_index: replaceModal.questionIndex,
          replacement_type: type
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedPaper((prev: any) => ({ ...prev, sections: data.sections }));
        setReplaceModal({ open: false, sectionIndex: 0, questionIndex: 0, question: null });
      } else {
        alert(data.message || 'Replacement failed');
      }
    } catch (err) {
      console.error('Replacement error:', err);
    }
  };

  // Finalize Paper
  const handleFinalize = async () => {
    if (!generatedPaper) return;
    try {
      const res = await fetchWithAuth(`/api/pyq-intelligence/paper/${generatedPaper.id}/finalize`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert('Paper finalized and approved successfully! Version 5 (Finalized) created.');
      }
    } catch (err) {
      console.error('Finalization error:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#4338CA] text-white p-6 rounded-2xl shadow-md border-l-4 border-amber-400">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-amber-300 text-xs font-black uppercase tracking-wider">
              <Brain className="w-4 h-4" />
              <span>Phase 19 Advanced Engine • District Nuh, Haryana</span>
            </div>
            <h1 className="text-xl md:text-2xl font-black text-white mt-1">
              PYQ Intelligence & Board Pattern Engine
            </h1>
            <p className="text-xs text-slate-200 mt-1 max-w-3xl font-medium">
              Comprehensive mathematical recurrence intelligence, multi-year occurrence trails, similarity clustering, board blueprints, and multi-mode paper generation with live marks balancing and AI quality scoring.
            </p>
          </div>

          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xs p-2 rounded-xl border border-white/20">
            <button
              onClick={() => setSelectedBoard(1)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === 1 ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              HBSE (Haryana)
            </button>
            <button
              onClick={() => setSelectedBoard(2)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === 2 ? 'bg-amber-400 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              CBSE (Central)
            </button>
          </div>
        </div>
      </div>

      {/* Global Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Class:</span>
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(Number(e.target.value))}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value={1}>Class 10 (Secondary)</option>
              <option value={2}>Class 12 (Senior Secondary)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Medium:</span>
            <select
              value={selectedMedium}
              onChange={e => setSelectedMedium(e.target.value)}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value="English">English Medium</option>
              <option value="Hindi">Hindi Medium (हिंदी)</option>
              <option value="Bilingual">Bilingual</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-bold flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 10-Tab Navigation Bar */}
      <div className="flex overflow-x-auto border-b border-slate-200 bg-white rounded-t-xl p-1 gap-1 text-xs font-bold shadow-xs">
        {[
          { id: 'overview', label: '1. Overview', icon: Brain, color: 'text-amber-400' },
          { id: 'repository', label: '2. PYQ Repository', icon: BookOpen, color: 'text-blue-400' },
          { id: 'chapter-analysis', label: '3. Chapter Analysis', icon: TrendingUp, color: 'text-orange-400' },
          { id: 'topic-analysis', label: '4. Topic Analysis', icon: FileSpreadsheet, color: 'text-teal-400' },
          { id: 'repeated', label: '5. Repeated / Similar', icon: Layers, color: 'text-purple-400' },
          { id: 'trends', label: '6. Multi-Year Trends', icon: Clock, color: 'text-indigo-400' },
          { id: 'patterns', label: '7. Board Blueprints', icon: ShieldCheck, color: 'text-emerald-400' },
          { id: 'blueprint', label: '8. Blueprint Builder', icon: Sliders, color: 'text-pink-400' },
          { id: 'generator', label: '9. Smart Paper Generator', icon: Sparkles, color: 'text-amber-300' },
          { id: 'history', label: '10. Version History', icon: History, color: 'text-slate-400' }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-3.5 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
                isActive ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${tab.color}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ----------------- TAB 1: OVERVIEW ----------------- */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
              <div className="text-slate-500 font-bold text-xs uppercase">Verified PYQs</div>
              <div className="text-2xl font-black text-slate-900">{overview.total_verified_pyqs}</div>
              <div className="text-[10px] text-emerald-600 font-bold">100% Verified Provenance</div>
            </div>

            <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
              <div className="text-slate-500 font-bold text-xs uppercase">Total Occurrences Tracked</div>
              <div className="text-2xl font-black text-indigo-900">{overview.total_occurrences}</div>
              <div className="text-[10px] text-indigo-600 font-bold">Multi-Year Appearance Records</div>
            </div>

            <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
              <div className="text-slate-500 font-bold text-xs uppercase">Chapter Coverage</div>
              <div className="text-2xl font-black text-emerald-900">
                {overview.chapter_coverage?.pct}%
              </div>
              <div className="text-[10px] text-slate-500">
                {overview.chapter_coverage?.covered} / {overview.chapter_coverage?.total} Curriculum Chapters
              </div>
            </div>

            <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
              <div className="text-slate-500 font-bold text-xs uppercase">High-Priority Focus Topics</div>
              <div className="text-2xl font-black text-amber-600">{overview.high_priority_topics?.length || 0}</div>
              <div className="text-[10px] text-amber-700 font-bold">Recurrent Board Patterns</div>
            </div>
          </div>

          {/* High Priority Topics Grid */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-600" />
              High-Priority Chapters & Topics for Board Examinations (Factual Analytics)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {overview.high_priority_topics?.map((top: any) => (
                <div key={top.id} className="p-3 bg-amber-50/50 rounded-xl border border-amber-200 space-y-1">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="font-bold text-amber-900 uppercase">Ch {top.chapter_number}</span>
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-950 rounded font-black font-mono">
                      {top.priority_level}
                    </span>
                  </div>
                  <div className="font-bold text-slate-900">{top.chapter_name}</div>
                  <div className="text-[11px] text-slate-600">Frequency Score: {top.frequency_score} / 100</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ----------------- TAB 2: PYQ REPOSITORY ----------------- */}
      {activeTab === 'repository' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by text, formula, sqrt / √, x², chapter..."
                className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border rounded-lg text-xs"
              />
            </div>
            <button
              onClick={loadData}
              className="px-4 py-1.5 bg-[#0F2C59] text-white rounded-lg text-xs font-bold"
            >
              Search
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {questions.map((q) => (
              <div key={q.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-1.5">
                    <span className="font-black text-slate-900">Q{q.question_number}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-bold">{q.marks}M</span>
                    <span className="text-[10px] bg-purple-100 text-purple-900 px-2 py-0.5 rounded font-bold">{q.question_type}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono">{q.set_name} • {q.year}</span>
                </div>

                <div className="font-bold text-slate-900 text-sm">{q.question_text_normalized}</div>

                {q.rendered_math && (
                  <div className="text-[11px] font-mono text-indigo-700 bg-indigo-50/50 p-2 rounded border border-indigo-100">
                    Math: {q.rendered_math}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 3: CHAPTER ANALYSIS ----------------- */}
      {activeTab === 'chapter-analysis' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
          <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-600" />
            Chapter-Wise Historical Recurrence Analysis
          </h3>
          <div className="divide-y divide-slate-100 text-xs">
            {frequencies.map((ch: any) => (
              <div key={ch.chapter_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 text-sm">Chapter {ch.chapter_number}: {ch.chapter_name}</div>
                  <div className="text-slate-500 text-[11px]">Total Marks Appeared: {ch.total_marks || 0}M • Appeared in {ch.years_count || 1} Examination Years</div>
                </div>
                <div className="text-right">
                  <div className="font-black text-indigo-900 text-base">{ch.question_count} Questions</div>
                  <div className="text-[10px] text-slate-400">Verified Board Papers</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 4: TOPIC ANALYSIS ----------------- */}
      {activeTab === 'topic-analysis' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
          <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-teal-600" />
            Topic-Wise PYQ Analysis & Practice Paper Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {frequencies.map((f: any, idx: number) => (
              <div key={f.chapter_id} className="p-3 bg-slate-50 rounded-xl border flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="font-bold text-slate-900">Ch {f.chapter_number}: {f.chapter_name}</div>
                  <div className="text-[10px] text-slate-500">{f.question_count} Questions In Bank</div>
                </div>
                <button
                  onClick={() => {
                    setActiveTab('generator');
                    setGenMode('PYQ_BASED');
                  }}
                  className="px-3 py-1.5 bg-[#0F2C59] hover:bg-blue-900 text-white rounded-lg font-bold text-xs shadow-xs"
                >
                  Generate Paper
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 5: REPEATED QUESTIONS & SIMILARITY ----------------- */}
      {activeTab === 'repeated' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-600" />
              Repeated & Similar Question Groups ({repeatedClusters.length} Clusters Detected)
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Questions grouped by mathematical concept equivalence, unchanged numerical values, or identical board pattern wording.
            </p>
          </div>

          <div className="space-y-4 text-xs">
            {repeatedClusters.map((grp: any) => (
              <div key={grp.group_id} className="bg-white p-5 rounded-2xl border border-purple-200 shadow-2xs space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-black text-purple-900 text-xs">{grp.group_id}</span>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-950 font-bold rounded text-[10px]">
                      {grp.classification}
                    </span>
                    <span className="text-slate-500 text-[11px]">Similarity: {grp.similarity_score}%</span>
                  </div>
                  <span className="text-slate-400 font-mono text-[10px]">Appeared: {grp.first_year}–{grp.last_year}</span>
                </div>

                <div className="space-y-2">
                  {grp.questions?.map((q: any) => (
                    <div key={q.id} className="p-3 bg-slate-50 rounded-xl border space-y-1">
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="font-bold text-slate-700">{q.set_name} ({q.year}) • {q.marks} Mark(s)</span>
                        <span className="text-emerald-700 font-black">{q.verification_status}</span>
                      </div>
                      <div className="font-medium text-slate-900">{q.question_text_normalized}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 6: MULTI-YEAR TRENDS ----------------- */}
      {activeTab === 'trends' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            Year-Wise Examination Question Count & Marks Trends (2020–2024)
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
            {trends.map((t: any) => (
              <div key={t.year} className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-200 space-y-1">
                <div className="text-xs font-black text-indigo-950">Year {t.year}</div>
                <div className="text-xl font-black text-slate-900">{t.question_count} Questions</div>
                <div className="text-[11px] text-slate-500">{t.total_marks} Marks Represented</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 7: BOARD PATTERNS ----------------- */}
      {activeTab === 'patterns' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Verified Board Exam Blueprints & Structure
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {patterns.map((p: any) => (
              <div key={p.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-bold text-slate-900 text-sm">{p.pattern_name}</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 font-bold rounded text-[10px]">
                    {p.total_marks} Marks • {p.duration_minutes} Mins
                  </span>
                </div>

                <div className="space-y-1.5">
                  {p.sections?.map((s: any) => (
                    <div key={s.name} className="flex justify-between p-2 bg-slate-50 rounded border">
                      <span className="font-bold text-slate-800">{s.name} ({s.type})</span>
                      <span className="font-mono text-slate-900">{s.question_count} Qs × {s.marks_per_question}M = {s.question_count * s.marks_per_question}M</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 8: BLUEPRINT BUILDER ----------------- */}
      {activeTab === 'blueprint' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex items-center justify-between border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Sliders className="w-4 h-4 text-pink-600" />
              Interactive Paper Blueprint Builder & Marks Balancer
            </h3>
            <span className="px-3 py-1 bg-pink-100 text-pink-900 rounded font-bold text-[10px]">
              Live Validation
            </span>
          </div>

          {blueprintValidationMsg && (
            <div className={`p-3 rounded-xl font-bold flex items-center gap-2 ${
              blueprintValidationMsg.valid ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {blueprintValidationMsg.valid ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span>{blueprintValidationMsg.text}</span>
            </div>
          )}

          <div className="space-y-3">
            {blueprintSections.map((sec, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-xl border grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                <div>
                  <label className="block text-slate-500 font-bold text-[10px]">Section Name</label>
                  <input
                    type="text"
                    value={sec.name}
                    onChange={e => {
                      const updated = [...blueprintSections];
                      updated[idx].name = e.target.value;
                      setBlueprintSections(updated);
                    }}
                    className="w-full p-1.5 bg-white border rounded font-bold"
                  />
                </div>

                <div>
                  <label className="block text-slate-500 font-bold text-[10px]">Question Count</label>
                  <input
                    type="number"
                    value={sec.question_count}
                    onChange={e => {
                      const updated = [...blueprintSections];
                      updated[idx].question_count = Number(e.target.value);
                      setBlueprintSections(updated);
                    }}
                    className="w-full p-1.5 bg-white border rounded font-bold"
                  />
                </div>

                <div>
                  <label className="block text-slate-500 font-bold text-[10px]">Marks Per Question</label>
                  <input
                    type="number"
                    value={sec.marks_per_question}
                    onChange={e => {
                      const updated = [...blueprintSections];
                      updated[idx].marks_per_question = Number(e.target.value);
                      setBlueprintSections(updated);
                    }}
                    className="w-full p-1.5 bg-white border rounded font-bold"
                  />
                </div>

                <div className="text-right font-mono font-bold text-slate-900 pt-3">
                  = {sec.question_count * sec.marks_per_question} Marks
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center pt-3 border-t">
            <div className="font-bold text-slate-700">
              Total Calculated: {blueprintSections.reduce((sum, s) => sum + (s.question_count * s.marks_per_question), 0)} / {blueprintTotalMarks} Marks
            </div>
            <button
              onClick={handleValidateBlueprint}
              className="px-5 py-2 bg-[#0F2C59] text-white font-black rounded-xl shadow-xs"
            >
              Validate Blueprint
            </button>
          </div>
        </div>
      )}

      {/* ----------------- TAB 9: SMART PAPER GENERATOR ----------------- */}
      {activeTab === 'generator' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border-2 border-purple-200 shadow-md space-y-4 text-xs">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-700" />
                <h3 className="text-base font-black text-slate-900">Intelligent Mathematics Paper Generator</h3>
              </div>
              <span className="px-3 py-1 bg-purple-100 text-purple-900 rounded-lg text-xs font-black">
                Phase 19 Multi-Mode
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Generator Mode</label>
                <select
                  value={genMode}
                  onChange={e => setGenMode(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="BOARD_PATTERN">1. Official Board Pattern</option>
                  <option value="PYQ_BASED">2. PYQ Based</option>
                  <option value="SMART_MIX">3. Smart Mix</option>
                  <option value="CUSTOM">4. Custom Blueprint</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Question Source Pool</label>
                <select
                  value={genSourceMix}
                  onChange={e => setGenSourceMix(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="PYQ_ONLY">100% Verified PYQ Only</option>
                  <option value="PYQ_VARIANT">PYQ + AI Generated Variants</option>
                  <option value="SMART_MIX">75% PYQ + 25% Question Bank</option>
                  <option value="NEW_ONLY">New Only</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">School Name</label>
                <input
                  type="text"
                  value={genSchoolName}
                  onChange={e => setGenSchoolName(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Exam Name</label>
                <input
                  type="text"
                  value={genExamName}
                  onChange={e => setGenExamName(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="px-6 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl font-black text-xs shadow-md flex items-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>Generate Intelligent Paper</span>
              </button>
            </div>
          </div>

          {/* Generated Paper Layout */}
          {generatedPaper && (
            <div className="bg-white p-8 rounded-2xl border-2 border-slate-300 shadow-lg space-y-6 font-serif text-slate-900">
              {/* Quality & Action Bar */}
              <div className="flex items-center justify-between border-b pb-4 not-italic font-sans">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-900 rounded-lg text-xs font-black">
                    Paper ID: {generatedPaper.id}
                  </span>

                  {qualityCheck && (
                    <span className={`px-3 py-1 rounded-lg text-xs font-black flex items-center gap-1 ${
                      qualityCheck.classification === 'GREEN' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'
                    }`}>
                      <Award className="w-3.5 h-3.5" />
                      <span>AI Quality Score: {qualityCheck.quality_score}% ({qualityCheck.classification})</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAnswerKey(!showAnswerKey)}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 rounded-lg text-xs font-bold border border-indigo-200"
                  >
                    {showAnswerKey ? 'Hide Answer Key' : 'Show Answer Key'}
                  </button>

                  <button
                    onClick={handleFinalize}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold"
                  >
                    Finalize Paper (v5)
                  </button>

                  <button
                    onClick={() => window.print()}
                    className="px-4 py-1.5 bg-slate-900 hover:bg-black text-white rounded-lg text-xs font-bold flex items-center gap-1"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span>Print / PDF</span>
                  </button>
                </div>
              </div>

              {/* School Header */}
              <div className="text-center space-y-1 border-b-2 border-slate-900 pb-4">
                <h2 className="text-lg font-bold uppercase tracking-wider">{generatedPaper.header?.school_name}</h2>
                <h3 className="text-sm font-semibold">{generatedPaper.header?.exam_name}</h3>
                <div className="flex justify-between items-center text-xs font-sans font-bold pt-2">
                  <span>Class: {generatedPaper.header?.class_name}</span>
                  <span>Subject: {generatedPaper.header?.subject} ({generatedPaper.header?.medium})</span>
                  <span>Max. Marks: {generatedPaper.header?.total_marks}</span>
                  <span>Time: {generatedPaper.header?.duration_minutes} Mins</span>
                </div>
              </div>

              {/* General Instructions */}
              <div className="text-xs space-y-1 bg-slate-50 p-3 rounded-lg border font-sans">
                <div className="font-bold">General Instructions:</div>
                <ol className="list-decimal list-inside space-y-0.5 text-slate-700">
                  {generatedPaper.instructions?.map((inst: string, idx: number) => (
                    <li key={idx}>{inst}</li>
                  ))}
                </ol>
              </div>

              {/* Sections */}
              <div className="space-y-6">
                {generatedPaper.sections?.map((sec: any, sIdx: number) => (
                  <div key={sec.section_name} className="space-y-3">
                    <div className="text-center border-b border-slate-300 pb-1">
                      <span className="font-bold text-xs uppercase tracking-wider bg-slate-100 px-3 py-1 rounded">
                        {sec.section_name} (Questions: {sec.question_count} × {sec.marks_per_question} Mark = {sec.question_count * sec.marks_per_question} Marks)
                      </span>
                    </div>

                    <div className="space-y-4 text-xs">
                      {sec.questions?.map((q: any, qIdx: number) => (
                        <div key={q.id || qIdx} className="flex justify-between items-start gap-4 p-2 hover:bg-slate-50 rounded group">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-start gap-2">
                              <span className="font-bold font-sans">Q{qIdx + 1}.</span>
                              <div className="space-y-0.5">
                                <p className="font-medium leading-relaxed">{q.question_text_normalized || q.question_text_original}</p>
                                {q.provenance_label && (
                                  <span className="text-[10px] text-purple-700 font-sans font-bold bg-purple-50 px-1.5 py-0.5 rounded">
                                    {q.provenance_label}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 font-sans not-italic">
                            <span className="font-bold text-slate-700">[{sec.marks_per_question}]</span>
                            <button
                              onClick={() => setReplaceModal({ open: true, sectionIndex: sIdx, questionIndex: qIdx, question: q })}
                              className="opacity-0 group-hover:opacity-100 px-2 py-0.5 bg-purple-100 hover:bg-purple-200 text-purple-900 rounded text-[10px] font-bold transition"
                            >
                              Replace
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Answer Key */}
              {showAnswerKey && (
                <div className="mt-8 pt-6 border-t-2 border-dashed border-indigo-300 bg-indigo-50/40 p-5 rounded-xl space-y-3 font-sans">
                  <div className="flex items-center justify-between border-b border-indigo-200 pb-2">
                    <h4 className="font-black text-indigo-950 text-xs uppercase tracking-wider">
                      Official Marking Scheme & Answer Key
                    </h4>
                    <span className="text-[10px] text-indigo-700 font-bold">Confidential — For Evaluator Only</span>
                  </div>

                  <div className="space-y-2 text-xs">
                    {generatedPaper.sections?.map((sec: any) =>
                      sec.questions?.map((q: any, qIdx: number) => (
                        <div key={q.id || qIdx} className="p-2 bg-white rounded border border-indigo-100">
                          <div className="font-bold text-slate-800">Q{qIdx + 1} ({sec.section_name}) — [{sec.marks_per_question}M]:</div>
                          <div className="text-slate-600 mt-0.5">{q.answer_key || q.solution_text || 'Final step-by-step verification according to Board Rubric.'}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB 10: VERSION HISTORY ----------------- */}
      {activeTab === 'history' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3 text-xs">
          <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-600" />
            Paper Version History Archive (math_paper_versions)
          </h3>
          <p className="text-slate-500">
            Immutable audit record of every paper generation lifecycle: Version 1 (Draft) → Version 2 (Edited) → Version 3 (AI Checked) → Version 5 (Finalized).
          </p>
        </div>
      )}

      {/* Replace Question Modal */}
      {replaceModal.open && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 text-xs">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="font-black text-slate-900 text-sm">Replace Question [{replaceModal.question?.marks}M]</h3>
              <button onClick={() => setReplaceModal({ open: false, sectionIndex: 0, questionIndex: 0, question: null })}>
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <p className="text-slate-600 font-medium">
              Choose replacement strategy while maintaining exact section mark allocation:
            </p>

            <div className="grid grid-cols-1 gap-2 pt-2">
              <button
                onClick={() => handleReplaceQuestion('PYQ')}
                className="p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl text-left font-bold text-purple-950 transition"
              >
                1. Replace with Verified PYQ Candidate
              </button>
              <button
                onClick={() => handleReplaceQuestion('SIMILAR')}
                className="p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl text-left font-bold text-blue-950 transition"
              >
                2. Replace with Similar Concept Candidate
              </button>
              <button
                onClick={() => handleReplaceQuestion('NEW')}
                className="p-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-left font-bold text-emerald-950 transition"
              >
                3. Replace with Question Bank Candidate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default PYQIntelligencePage;
